import gravatar from "gravatar";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiCard,
  EuiText,
  EuiAvatar,
  EuiIcon,
  EuiLink,
  EuiBadge,
  EuiSpacer,
} from "@elastic/eui";
import { KeycloakUser } from "../../../";
import { useAnketaI18n } from "src/internationalization";

type ContactType = "sales" | "technical" | "customer";

interface ContactGroup {
  type: ContactType;
  users: KeycloakUser[];
}

interface ApplianceUserCardsProps {
  groups: ContactGroup[];
}

const COLOR_MAP: Record<ContactType, string> = {
  sales: "#006BB4",
  technical: "#017D73",
  customer: "#BD271E",
};

const getUserInitials = (user: KeycloakUser): string => {
  const first = user.firstName?.charAt(0) ?? "";
  const last = user.lastName?.charAt(0) ?? "";
  return (first + last).toUpperCase() || user.email.charAt(0).toUpperCase();
};

const getUserAttribute = (
  user: KeycloakUser,
  attr: string,
): string | undefined => {
  const fromAttributes = (user.attributes as any)?.[attr];
  if (fromAttributes) return fromAttributes;
  const fromSchema = (user.profileSchema as any)?.[attr];
  if (fromSchema) return fromSchema;
  return undefined;
};

const UserCard = ({
  user,
  accentColor,
}: {
  user: KeycloakUser;
  accentColor: string;
}): JSX.Element => {
  const fullName =
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;
  const jobTitle = getUserAttribute(user, "jobTitle");
  const businessPhone = getUserAttribute(user, "businessPhone");

  return (
    <EuiCard
      layout="horizontal"
      icon={
        <EuiAvatar
          name={getUserInitials(user)}
          size="xl"
          color={accentColor}
          imageUrl={gravatar.url(user.email ?? "", { d: "mp", s: "80" })}
        />
      }
      title={
        <EuiFlexGroup direction="column" gutterSize="none">
          <EuiFlexItem>
            <EuiText size="m">
              <strong>{fullName}</strong>
            </EuiText>
          </EuiFlexItem>
          {jobTitle && (
            <EuiFlexItem>
              <EuiFlexGroup
                gutterSize="xs"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiIcon type="users" size="s" />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size="xs" color="subdued">
                    {jobTitle}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      }
      description={
        <EuiFlexGroup
          direction="column"
          gutterSize="s"
          style={{ marginTop: 8 }}
        >
          <EuiFlexItem>
            <EuiFlexGroup
              gutterSize="xs"
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiIcon type="email" size="m" />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiLink href={`mailto:${user.email}`}>
                  <EuiText size="s">{user.email}</EuiText>
                </EuiLink>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>

          {businessPhone && (
            <EuiFlexItem>
              <EuiFlexGroup
                gutterSize="xs"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  <EuiIcon type="mobile" size="m" />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiLink href={`tel:${businessPhone}`}>
                    <EuiText size="s">{businessPhone}</EuiText>
                  </EuiLink>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      }
    />
  );
};

export const ApplianceUserCards = ({
  groups,
}: ApplianceUserCardsProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const nonEmpty = groups.filter((g) => g.users.length > 0);

  const LABEL_MAP: Record<ContactType, string> = {
    sales: translate("anketa.opsanio.appliance.flyout.users.group.sales", {
      defaultMessage: "Sales",
    }),
    technical: translate(
      "anketa.opsanio.appliance.flyout.users.group.technical",
      { defaultMessage: "Technical" },
    ),
    customer: translate(
      "anketa.opsanio.appliance.flyout.users.group.other",
      { defaultMessage: "Other" },
    ),
  };

  if (nonEmpty.length === 0) {
    return (
      <EuiText size="s" color="subdued">
        {translate("anketa.opsanio.appliance.flyout.users.empty", {
          defaultMessage: "No contacts assigned.",
        })}
      </EuiText>
    );
  }

  return (
    <EuiFlexGroup direction="column" gutterSize="l">
      {nonEmpty.map(({ type, users }) => {
        const label = LABEL_MAP[type];
        const color = COLOR_MAP[type];
        return (
          <EuiFlexItem key={type}>
            {/* Section header */}
            <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiBadge color={color}>{label}</EuiBadge>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs" color="subdued">
                  {translate("anketa.opsanio.appliance.flyout.users.count", {
                    values: { count: users.length },
                    defaultMessage: "{{count}} contact(s)",
                  })}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />

            <EuiFlexGroup direction="column" gutterSize="s">
              {users.map((user) => (
                <EuiFlexItem key={user.id}>
                  <UserCard user={user} accentColor={color} />
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
          </EuiFlexItem>
        );
      })}
    </EuiFlexGroup>
  );
};
